import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";
import "./PregledDokumenata.css";

/* Preglednik dokumenata preko celog ekrana, sa listanjem levo-desno.

   Zašto uopšte: dokumenta se do sada otvarala svako u svom tabu, pa je pregled
   deset priloga značio deset tabova i deset povrataka nazad.

   Format se ne pretpostavlja nego čita iz imena fajla:
     - slike (jpg, png…)  → prikazuju se direktno
     - PDF                → u okviru, pregledač ga sam iscrtava
     - sve ostalo (doc, docx, xls…) → pregledač to ne ume da prikaže, pa se
       nudi preuzimanje; bolje jasna poruka nego prazan beli okvir

   Preuzimanje ide preko blob-a, ne preko <a download>: dokumenta servira
   backend sa druge adrese, a atribut "download" se kod druge adrese ignoriše —
   fajl bi se otvorio umesto da se sačuva, i to pod pogrešnim imenom. */

const SLIKE = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];

const ekstenzija = (ime) => String(ime || "").split(".").pop().toLowerCase();

const PregledDokumenata = ({ dokumenta = [], indeks, naslov, onIndeks, onZatvori }) => {
    const { t } = useTranslation();

    const tekuci = indeks != null ? dokumenta[indeks] : null;
    const ext = useMemo(() => ekstenzija(tekuci?.fajl), [tekuci]);

    const imaPrethodni = indeks > 0;
    const imaSledeci = indeks != null && indeks < dokumenta.length - 1;

    /* Tastatura: strelice listaju, Esc zatvara — isto kao pregled slika u čatu. */
    useEffect(() => {
        if (tekuci == null) return;
        const naTaster = (e) => {
            if (e.key === "ArrowLeft" && imaPrethodni) onIndeks(indeks - 1);
            else if (e.key === "ArrowRight" && imaSledeci) onIndeks(indeks + 1);
            else if (e.key === "Escape") onZatvori();
        };
        window.addEventListener("keydown", naTaster);
        return () => window.removeEventListener("keydown", naTaster);
    }, [tekuci, indeks, imaPrethodni, imaSledeci, onIndeks, onZatvori]);

    if (!tekuci) return null;

    const preuzmi = async () => {
        try {
            const odgovor = await fetch(tekuci.url);
            if (!odgovor.ok) throw new Error("HTTP " + odgovor.status);
            saveAs(await odgovor.blob(), tekuci.fajl || "dokument");
        } catch {
            /* Ako preuzimanje padne (mreža, fajl obrisan sa diska), bar da se
               otvori — bolje nego da klik ne uradi ništa. */
            window.open(tekuci.url, "_blank");
        }
    };

    const telo = () => {
        if (SLIKE.includes(ext)) {
            return <img src={tekuci.url} alt={tekuci.fajl} onClick={(e) => e.stopPropagation()} />;
        }
        if (ext === "pdf") {
            return (
                <iframe
                    title={tekuci.fajl}
                    src={tekuci.url}
                    onClick={(e) => e.stopPropagation()}
                />
            );
        }
        return (
            <div className="pd-bez-pregleda" onClick={(e) => e.stopPropagation()}>
                <i className="pi pi-file" />
                <p>{t("dok.bezPregleda", { format: ext ? ext.toUpperCase() : "?" })}</p>
                <button type="button" className="pd-dugme pd-dugme-glavno" onClick={preuzmi}>
                    <i className="pi pi-download" /> {t("dok.preuzmi")}
                </button>
            </div>
        );
    };

    return (
        <div className="pd-omot" onClick={onZatvori}>
            <div className="pd-traka" onClick={(e) => e.stopPropagation()}>
                {/* Levo prazno, sredina naslov, desno radnje — da naslov ostane
                    stvarno centriran bez obzira na širinu dugmadi. */}
                <div className="pd-traka-strana" />

                <div className="pd-naslov">
                    {naslov && <span className="pd-registracija">{naslov}</span>}
                    <span className="pd-naziv">{tekuci.naziv}</span>
                    <span className="pd-fajl">{tekuci.fajl}</span>
                </div>

                <div className="pd-traka-strana pd-radnje">
                    <span className="pd-brojac">{indeks + 1} / {dokumenta.length}</span>
                    <button type="button" className="pd-dugme" onClick={preuzmi} title={t("dok.preuzmi")}>
                        <i className="pi pi-download" />
                    </button>
                    <button type="button" className="pd-dugme" onClick={onZatvori} title={t("dok.zatvori")}>
                        <i className="pi pi-times" />
                    </button>
                </div>
            </div>

            {imaPrethodni && (
                <button type="button" className="pd-strelica pd-levo" title={t("dok.prethodni")}
                        onClick={(e) => { e.stopPropagation(); onIndeks(indeks - 1); }}>
                    <i className="pi pi-chevron-left" />
                </button>
            )}

            <div className="pd-telo">{telo()}</div>

            {imaSledeci && (
                <button type="button" className="pd-strelica pd-desno" title={t("dok.sledeci")}
                        onClick={(e) => { e.stopPropagation(); onIndeks(indeks + 1); }}>
                    <i className="pi pi-chevron-right" />
                </button>
            )}
        </div>
    );
};

export default PregledDokumenata;
